import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Bell, UserPlus, Clock, AlertTriangle, CheckCheck } from 'lucide-react';

const iconFor = (type) => {
  if (type === 'new_lead') return UserPlus;
  if (type === 'late') return Clock;
  return AlertTriangle;
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

export function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try { const r = await notificationAPI.list(); setItems(r.data); } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    if (!user) return;
    load();
    const id = setInterval(load, 45000); // poll for new alerts
    return () => clearInterval(id);
  }, [user, load]);

  const unread = items.filter(n => !n.read);

  const handleClick = async (n) => {
    setOpen(false);
    if (!n.read) {
      setItems(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
      try { await notificationAPI.markRead(n.id); } catch (e) { console.error(e); }
    }
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    setItems(prev => prev.map(x => ({ ...x, read: true })));
    try { await notificationAPI.markAllRead(); } catch (e) { console.error(e); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative h-9 w-9 rounded-full hover:bg-muted flex items-center justify-center transition-colors" data-testid="notification-bell">
          <Bell size={18} className="text-muted-foreground" />
          {unread.length > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-semibold flex items-center justify-center" data-testid="notification-count">
              {unread.length > 9 ? '9+' : unread.length}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-4 py-2.5 border-b">
          <span className="font-medium text-sm">Alerts {unread.length > 0 && <span className="text-muted-foreground">({unread.length} new)</span>}</span>
          {unread.length > 0 && (
            <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={markAll} data-testid="notif-mark-all">
              <CheckCheck size={13} /> Mark all read
            </Button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No alerts yet.</p>
          ) : (
            items.slice(0, 20).map(n => {
              const Icon = iconFor(n.type);
              return (
                <button
                  key={n.id}
                  onClick={() => handleClick(n)}
                  className={`w-full text-left flex items-start gap-3 px-4 py-3 border-b last:border-0 hover:bg-muted/60 transition-colors ${!n.read ? 'bg-primary/5' : ''}`}
                  data-testid={`notif-item-${n.id}`}
                >
                  <Icon size={15} className={`mt-0.5 shrink-0 ${n.type === 'new_lead' ? 'text-indigo-600' : n.type === 'late' ? 'text-amber-600' : 'text-blue-600'}`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm leading-snug ${!n.read ? 'font-medium' : 'text-muted-foreground'}`}>{n.message}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{timeAgo(n.created_at)}{n.link ? ' · tap to view' : ''}</p>
                  </div>
                  {!n.read && <span className="h-2 w-2 rounded-full bg-primary mt-1.5 shrink-0" />}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
